import { useEffect, useRef, useState } from "react";

import { Column, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetCandidacyPartialCountsByStatus } from "@/hooks/queries/useGetCandidacyPartialCountsByStatus";
import {
  candidacyStatusToPipelineStage,
  pipelineStageOrder,
  pipelineStageToKanbanToneClass,
  pipelineStageToLabel,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { CandidacyStatus, PipelineStage } from "@/types";

const EMPTY_SEGMENT_FLEX = 0;
const MIN_PILL_WRAPPER_WIDTH_PX = 40;
const MIN_PILL_WRAPPER_WIDTH_CLASS = "min-w-10";
const MIN_VISIBLE_LABEL_CHARS = 5;
const APPROX_MONO_CHAR_WIDTH_PX = 6;
const MIN_WIDTH_TO_SHOW_LABEL =
  MIN_VISIBLE_LABEL_CHARS * APPROX_MONO_CHAR_WIDTH_PX;

const getStageCounts = (countsByStatus: Record<string, number>) => {
  const counts = Object.fromEntries(
    pipelineStageOrder.map((stage) => [stage, 0]),
  ) as Record<PipelineStage, number>;

  for (const [status, count] of Object.entries(countsByStatus)) {
    const stage = candidacyStatusToPipelineStage[status as CandidacyStatus];
    if (!stage) continue;
    counts[stage] += count;
  }

  return counts;
};

const PipelineStagePill = ({
  count,
  flexGrow,
  stage,
}: {
  count: number;
  flexGrow: number;
  stage: PipelineStage;
}) => {
  const pillRef = useRef<HTMLDivElement>(null);
  const [showLabel, setShowLabel] = useState(true);
  const label = pipelineStageToLabel[stage];

  useEffect(() => {
    const element = pillRef.current;
    if (!element) return;

    const updateLabelVisibility = () => {
      setShowLabel(count > 0 && element.offsetWidth >= MIN_WIDTH_TO_SHOW_LABEL);
    };

    updateLabelVisibility();

    const resizeObserver = new ResizeObserver(updateLabelVisibility);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [count]);

  const isEmpty = count === 0;

  return (
    <div
      className={cn("min-w-0", MIN_PILL_WRAPPER_WIDTH_CLASS)}
      style={{
        flex: `${flexGrow.toFixed(2)} 1 ${MIN_PILL_WRAPPER_WIDTH_PX.toString()}px`,
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={pillRef}
            className={cn(
              "h-full min-w-0",
              isEmpty && "flex items-center justify-center",
            )}
          >
            <Row
              className={cn(
                "items-center justify-center overflow-hidden border border-transparent transition-colors hover:border-slate-300/80",
                isEmpty
                  ? "aspect-square h-6 rounded-full"
                  : "h-full min-w-0 rounded-full px-1",
                pipelineStageToKanbanToneClass[stage],
              )}
            >
              {showLabel ? (
                <span className="w-full truncate text-center text-xs font-medium leading-none text-slate-900">
                  {label}
                </span>
              ) : null}
            </Row>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-sm">
            {label}: {count.toLocaleString()}
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export const JobCandidacyCountsByStatus = ({
  className,
}: {
  className?: string;
}) => {
  const {
    data: candidacyCountsByStatus,
    isPending: isCandidacyCountsByStatusPending,
    isError: isCandidacyCountsByStatusError,
  } = useGetCandidacyPartialCountsByStatus();

  const render = () => {
    if (isCandidacyCountsByStatusPending) {
      return (
        <Row className={cn("items-center", className)}>
          <LoadingIndicator variant="medium" />
        </Row>
      );
    }

    if (isCandidacyCountsByStatusError) {
      return (
        <Row className={cn("items-center", className)}>
          <ErrorText errorMessage="Failed to load candidacy counts" />
        </Row>
      );
    }

    const countsByStage = getStageCounts(candidacyCountsByStatus);
    const totalCandidates = Object.values(countsByStage).reduce(
      (total, count) => total + count,
      0,
    );

    return (
      <Column className={cn("w-full min-w-0 gap-2", className)}>
        <TooltipProvider delayDuration={0}>
          <div className="flex h-10 w-full min-w-0 items-stretch gap-0.5 overflow-hidden">
            {pipelineStageOrder.map((stage) => {
              const count = countsByStage[stage];
              const flexGrow = count > 0 ? count : EMPTY_SEGMENT_FLEX;

              return (
                <PipelineStagePill
                  key={stage}
                  count={count}
                  flexGrow={flexGrow}
                  stage={stage}
                />
              );
            })}
          </div>
        </TooltipProvider>
        <div className="w-full text-right">
          <Text className="text-xs" variant="muted">
            {totalCandidates.toLocaleString()} candidate
            {totalCandidates === 1 ? "" : "s"}
          </Text>
        </div>
      </Column>
    );
  };

  return render();
};
